<template>
  <div class="user-dashboard">
    <!-- 现代化页面头部 -->
    <div class="modern-header">
      <div class="header-background">
        <div class="header-pattern"></div>
      </div>
      <div class="header-content">
        <div class="header-left">
          <el-button 
            type="text" 
            @click="handleBack"
            class="back-button"
          >
            <i class="el-icon-arrow-left"></i>
            <span>返回</span>
          </el-button>
          <div class="page-title">
            <h1>个人信息</h1>
            <p class="page-subtitle">管理您的账户信息和安全设置</p>
          </div>
        </div>
        <div class="header-right">
          <div class="user-avatar">
            <i class="el-icon-user-solid"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">

      <!-- 详细信息卡片 -->
      <el-card class="user-info-card">
        <div slot="header" class="card-header">
          <div class="header-title">
            <i class="el-icon-s-custom"></i>
            <span>账户详情</span>
          </div>
        </div>
        
        <div class="info-sections">
          <!-- 基本信息 -->
          <div class="info-section">
            <div class="section-title">
              <i class="el-icon-user"></i>
              <span>基本信息</span>
            </div>
            
            <div class="user-info-item">
              <div class="info-label">
                <i class="el-icon-user"></i>
                <span>用户名</span>
              </div>
              <div class="info-content">
                <div class="info-value">{{ username || '加载中...' }}</div>
              </div>
            </div>
            
            <div class="user-info-item">
              <div class="info-label">
                <i class="el-icon-message"></i>
                <span>邮箱地址</span>
              </div>
              <div class="info-content">
                <div class="info-value">{{ email || '加载中...' }}</div>
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="handleModifyEmail"
                  class="action-btn"
                >
                  <i class="el-icon-edit"></i>
                  修改
                </el-button>
              </div>
            </div>
          </div>

          <!-- 安全信息 -->
          <div class="info-section">
            <div class="section-title">
              <i class="el-icon-lock"></i>
              <span>安全信息</span>
            </div>
            
            <div class="user-info-item private-key-item">
              <div class="info-label">
                <i class="el-icon-key"></i>
                <span>私钥</span>
              </div>
              <div class="info-content private-key-content">
                <div class="private-key-container">
                  <div class="private-key-input-wrapper">
                    <input 
                      type="text" 
                      :value="displayPrivateKey"
                      readonly 
                      class="private-key-input"
                    >
                    <div class="private-key-actions">
                      <button 
                        @click="togglePrivateKeyVisibility"
                        class="action-icon-btn"
                        :title="isPrivateKeyVisible ? '隐藏私钥' : '显示私钥'"
                      >
                        <i :class="isPrivateKeyVisible ? 'el-icon-view' : 'el-icon-lock'"></i>
                      </button>
                      <button 
                        @click="copyToClipboard"
                        class="action-icon-btn copy-btn"
                        title="复制到剪贴板"
                      >
                        <i class="el-icon-document-copy"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="security-warning">
                  <i class="el-icon-success"></i>
                  <span>请妥善保管您的私钥，不要泄露给他人</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 快速操作卡片 -->
      <el-card class="quick-actions-card">
        <div slot="header" class="card-header">
          <div class="header-title">
            <i class="el-icon-s-operation"></i>
            <span>快速操作</span>
          </div>
        </div>
        <div class="quick-actions">
          <div class="action-item" @click="handleModifyEmail">
            <div class="action-icon">
              <i class="el-icon-edit"></i>
            </div>
            <div class="action-content">
              <h4>修改邮箱</h4>
              <p>更新您的邮箱地址</p>
            </div>
          </div>
          <!-- <div class="action-item">
            <div class="action-icon">
              <i class="el-icon-refresh"></i>
            </div>
            <div class="action-content">
              <h4>重置私钥</h4>
              <p>生成新的私钥</p>
            </div>
          </div> -->
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'UserDetails',
  data() {
    return {
      isPrivateKeyVisible: false,
    }
  },
  computed: {
    ...mapGetters([
      'username',
      'email',
      'privateKey'
    ]),
    displayPrivateKey() {
      // 检查privateKey是否存在且不为空，否则使用默认的模拟数据
      if (!this.privateKey || this.privateKey.trim() === '') {
        // 由于可能存在数据加载延迟，这里提供一个临时的模拟数据
        return this.isPrivateKeyVisible ? 'demo-private-key-for-preview-only' : '••••••••'
      }
      return this.isPrivateKeyVisible ? this.privateKey : '••••••••'
    }
  },
  methods: {
    handleBack() {
      // 返回上一页或默认页面
      if (this.$route.query.redirect) {
        this.$router.push(this.$route.query.redirect)
      } else {
        this.$router.push('/')
      }
    },
    
    togglePrivateKeyVisibility() {
      this.isPrivateKeyVisible = !this.isPrivateKeyVisible
    },
    
    async copyToClipboard() {
      try {
        // 使用实际的privateKey或模拟数据
        const keyToCopy = this.privateKey && this.privateKey.trim() !== '' ? 
                          this.privateKey : 'demo-private-key-for-preview-only'
        
        await navigator.clipboard.writeText(keyToCopy)
        this.$message({
          message: '私钥已复制到剪贴板',
          type: 'success',
          duration: 2000,
          showClose: true
        })
      } catch (error) {
        console.error('复制失败:', error)
        this.$message({
          message: '复制失败，请手动复制',
          type: 'error',
          duration: 3000,
          showClose: true
        })
      }
    },
    
    handleModifyEmail() {
      // 跳转到修改邮箱页面
      this.$router.push('/change-email')
    }
  }
}
</script>

<style scoped>
/* 全局样式 */
.user-dashboard {
  min-height: 100vh;
  background-color: #f5f7fa;
  position: relative;
}

/* 现代化头部样式 */
.modern-header {
  position: relative;
  z-index: 1;
  padding: 0;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  background-color: #556dda;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-content {
  position: relative;
  z-index: 2;
  padding: 30px 20px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  backdrop-filter: blur(10px);
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.page-title h1 {
  margin: 0;
  color: white;
  font-size: 28px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.page-subtitle {
  margin: 8px 0 0 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 400;
}

.header-right {
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.user-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.3);
}

.user-avatar i {
  font-size: 20px;
  color: white;
}

/* 主要内容区域 */
.main-content {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 30px auto 0;
  padding: 0 20px 40px;
}


/* 用户信息卡片 */
.user-info-card,
.quick-actions-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  border: none;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.user-info-card:hover,
.quick-actions-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
}

/* 卡片头部 */
.card-header {
  background: linear-gradient(135deg, #f7fafc, #edf2f7);
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin: 0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #2d3748;
  font-size: 18px;
  font-weight: 600;
}

.header-title i {
  color: #667eea;
  font-size: 20px;
}

/* 信息区域 */
.info-sections {
  padding: 24px;
}

.info-section {
  margin-bottom: 32px;
}

.info-section:last-child {
  margin-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
  color: #4a5568;
  font-size: 16px;
  font-weight: 600;
}

.section-title i {
  color: #667eea;
  font-size: 18px;
}

/* 用户信息项 */
.user-info-item {
  display: flex;
  align-items: flex-start;
  padding: 20px 0;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.3s ease;
}

.user-info-item:last-child {
  border-bottom: none;
}

.user-info-item:hover {
  background: rgba(102, 126, 234, 0.02);
  border-radius: 8px;
  padding: 20px 16px;
  margin: 0 -16px;
}

.info-label {
  width: 140px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  font-weight: 500;
  font-size: 14px;
  flex-shrink: 0;
}

.info-label i {
  color: #667eea;
  font-size: 16px;
}

.info-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.info-value {
  color: #2d3748;
  font-size: 14px;
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  word-break: break-all;
  transition: all 0.3s ease;
}

.info-value:hover {
  border-color: #cbd5e0;
  background: #edf2f7;
}

/* 操作按钮 */
.action-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

/* 私钥相关样式 */
.private-key-content {
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.private-key-container {
  width: 100%;
}

.private-key-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
  overflow: hidden;
}

.private-key-input-wrapper:hover {
  border-color: #667eea;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.1);
}

.private-key-input-wrapper:focus-within {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.private-key-input {
  flex: 1;
  padding: 16px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #2d3748;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Courier New', monospace;
  letter-spacing: 0.5px;
  outline: none;
  min-height: 24px;
}

.private-key-actions {
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;
}

.action-icon-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #718096;
}

.action-icon-btn:hover {
  background: #e2e8f0;
  color: #667eea;
  transform: translateY(-1px);
}

.copy-btn:hover {
  background: #c6f6d5;
  color: #38a169;
}

.action-icon-btn i {
  font-size: 16px;
}

/* 安全警告 */
.security-warning {
  color: #67c23a;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0f9ff;
  border-left: 4px solid #67c23a;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
}

.security-warning i {
  font-size: 16px;
  flex-shrink: 0;
}

/* 快速操作卡片 */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding: 24px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-item:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.action-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.action-content h4 {
  margin: 0 0 4px 0;
  color: #2d3748;
  font-size: 14px;
  font-weight: 600;
}

.action-content p {
  margin: 0;
  color: #718096;
  font-size: 12px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 20px;
    text-align: center;
    padding: 25px 15px;
  }
  
  .header-left {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
    width: 100%;
  }
  
  .page-title {
    text-align: center;
    width: 100%;
  }
  
  .page-title h1 {
    font-size: 24px;
  }
  
  .page-subtitle {
    font-size: 13px;
  }
  
  .back-button {
    align-self: flex-start;
    margin-bottom: 10px;
  }
  
  .main-content {
    margin: 20px auto 0;
    padding: 0 15px 30px;
  }
  
  .info-sections {
    padding: 20px;
  }
  
  .user-info-item {
    flex-direction: column;
    gap: 12px;
    padding: 16px 0;
  }
  
  .info-label {
    width: auto;
    margin-bottom: 8px;
    justify-content: flex-start;
  }
  
  .info-content {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .action-btn {
    align-self: flex-start;
  }
  
  .private-key-content {
    width: 100%;
  }
  
  .private-key-input-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  
  .private-key-input {
    padding: 12px 16px;
    font-size: 13px;
  }
  
  .private-key-actions {
    padding: 8px 12px;
    justify-content: center;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
  }
  
  .action-icon-btn {
    width: 32px;
    height: 32px;
  }
  
  .quick-actions {
    grid-template-columns: 1fr;
    padding: 20px;
    gap: 12px;
  }
  
  .action-item {
    padding: 16px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .action-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 20px 12px;
  }
  
  .page-title h1 {
    font-size: 20px;
  }
  
  .page-subtitle {
    font-size: 12px;
  }
  
  .back-button {
    font-size: 13px;
    padding: 6px 12px;
  }
  
  .user-avatar {
    width: 40px;
    height: 40px;
  }
  
  .user-avatar i {
    font-size: 16px;
  }
  
  .main-content {
    padding: 0 12px 20px;
  }
  
  .info-sections {
    padding: 16px;
  }
  
  .section-title {
    font-size: 15px;
    margin-bottom: 16px;
  }
  
  .info-label {
    font-size: 13px;
  }
  
  .info-value {
    font-size: 13px;
    padding: 6px 10px;
  }
  
  .action-btn {
    font-size: 11px;
    padding: 6px 12px;
  }
  
  .private-key-input {
    font-size: 12px;
    padding: 10px 12px;
  }
  
  .action-icon-btn {
    width: 28px;
    height: 28px;
  }
  
  .action-icon-btn i {
    font-size: 13px;
  }
  
  .security-warning {
    font-size: 12px;
    padding: 10px 12px;
  }
  
  .quick-actions {
    padding: 16px;
  }
  
  .action-item {
    padding: 14px;
  }
  
  .action-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  
  .action-content h4 {
    font-size: 13px;
  }
  
  .action-content p {
    font-size: 11px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 360px) {
  .header-content {
    padding: 18px 10px;
  }
  
  .page-title h1 {
    font-size: 18px;
  }
  
  .page-subtitle {
    font-size: 11px;
  }
  
  .back-button {
    font-size: 12px;
    padding: 5px 10px;
  }
  
  .user-avatar {
    width: 36px;
    height: 36px;
  }
  
  .user-avatar i {
    font-size: 14px;
  }
  
  .main-content {
    padding: 0 10px 15px;
  }
  
  .info-sections {
    padding: 12px;
  }
  
  .section-title {
    font-size: 14px;
    margin-bottom: 12px;
  }
  
  .info-label {
    font-size: 12px;
  }
  
  .info-value {
    font-size: 12px;
    padding: 5px 8px;
  }
  
  .action-btn {
    font-size: 10px;
    padding: 5px 10px;
  }
  
  .private-key-input {
    font-size: 11px;
    padding: 8px 10px;
  }
  
  .action-icon-btn {
    width: 26px;
    height: 26px;
  }
  
  .action-icon-btn i {
    font-size: 12px;
  }
  
  .security-warning {
    font-size: 11px;
    padding: 8px 10px;
  }
  
  .quick-actions {
    padding: 12px;
  }
  
  .action-item {
    padding: 12px;
  }
  
  .action-icon {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  
  .action-content h4 {
    font-size: 12px;
  }
  
  .action-content p {
    font-size: 10px;
  }
}
</style>